import clsx from 'clsx';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PlateElement, useEditorRef } from 'platejs/react';
import { useComboboxInput, useHTMLInputCursorState } from '@platejs/combobox/react';
import { EmojiInlineLibrary, EmojiFloatingIndexSearch } from '@platejs/emoji';
import styles from './EmojiInputElement.module.scss';

// The :-emoji combobox. EmojiPlugin inserts an `emoji_input` void inline node
// when the user types `:` at a word boundary (triggerPreviousCharPattern: /^\s?$/
// means only fires at start-of-block or after whitespace — URLs/times/ratios
// are safe). This component renders for that node — a hidden-width query input
// plus a floating emoji list portaled to document.body.
//
// Architecture mirrors SlashInputElement exactly:
//   - useComboboxInput: handles blur/escape/backspace cancellation + removeInput
//   - useHTMLInputCursorState: feeds cursor position for backspace-at-start
// On select, removeInput removes the emoji_input node then calls insertEmoji
// which inserts { text: emoji.skins[0].native } — plain unicode text.
//
// @emoji-mart/data (432 kB JSON) is lazy-loaded on first open via dynamic
// import so it never bloats the main editor bundle. The library is cached in
// module scope after the first load.

const DROPDOWN_WIDTH_PX = 280;
const DROPDOWN_MAX_HEIGHT_PX = 260;
const DROPDOWN_GAP_PX = 6;
const VIEWPORT_MARGIN_PX = 8;
const MAX_RESULTS = 30;

interface DropdownPos {
    left: number;
    top?: number;
    bottom?: number;
}

function computePos(anchor: DOMRect): DropdownPos {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const maxLeft = vw - DROPDOWN_WIDTH_PX - VIEWPORT_MARGIN_PX;
    const left = Math.max(VIEWPORT_MARGIN_PX, Math.min(anchor.left, maxLeft));
    const spaceAbove = anchor.top;
    const spaceBelow = vh - anchor.bottom;
    if (spaceAbove >= DROPDOWN_MAX_HEIGHT_PX || spaceAbove >= spaceBelow) {
        return { left, bottom: vh - anchor.top + DROPDOWN_GAP_PX };
    }
    return { left, top: anchor.bottom + DROPDOWN_GAP_PX };
}

// Module-level cache — avoids re-initialising the library on every combobox open.
let emojiLibraryCache: InstanceType<typeof EmojiInlineLibrary> | null = null;

async function getEmojiLibrary(): Promise<InstanceType<typeof EmojiInlineLibrary>> {
    if (emojiLibraryCache) return emojiLibraryCache;
    // Dynamic import keeps the 432 kB JSON out of the initial editor bundle.
    const data = await import(/* webpackChunkName: "emoji-mart-data" */ '@emoji-mart/data');
    const emojiData = (data as any).default ?? data;
    emojiLibraryCache = new EmojiInlineLibrary(emojiData);
    return emojiLibraryCache;
}

type EmojiResult = {
    id: string;
    native: string;
    name: string;
};

export function EmojiInputElement(props: any) {
    const editor = useEditorRef();

    const inputRef = useRef<HTMLInputElement>(null);
    const wrapRef = useRef<HTMLSpanElement>(null);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const [pos, setPos] = useState<DropdownPos | null>(null);
    const [library, setLibrary] = useState<InstanceType<typeof EmojiInlineLibrary> | null>(null);

    const cursorState = useHTMLInputCursorState(inputRef);
    const { props: inputProps, removeInput } = useComboboxInput({
        ref: inputRef,
        cursorState,
        cancelInputOnBlur: true,
    });

    // Lazy-load emoji data on first mount of the combobox.
    useEffect(() => {
        let cancelled = false;
        getEmojiLibrary().then((lib) => {
            if (!cancelled) setLibrary(lib);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const indexSearch = useMemo(() => (library ? new EmojiFloatingIndexSearch(library) : null), [library]);

    const filtered = useMemo((): EmojiResult[] => {
        if (!indexSearch || !query) return [];
        const emojis = indexSearch.search(query).get();
        return emojis.slice(0, MAX_RESULTS).map((e: any) => ({
            id: e.id,
            native: e.skins?.[0]?.native ?? '',
            name: e.name,
        }));
    }, [indexSearch, query]);

    useLayoutEffect(() => {
        const update = () => {
            const el = wrapRef.current;
            if (!el) return;
            setPos(computePos(el.getBoundingClientRect()));
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [query, filtered.length]);

    const selectEmoji = useCallback(
        (emoji: EmojiResult) => {
            removeInput(true);
            // insertEmoji uses editor.getOptions(EmojiPlugin).createEmojiNode which
            // defaults to ({ skins }) => ({ text: skins[0].native }). We bypass it
            // and insert the text node directly to avoid a second EmojiPlugin
            // options lookup — same net result: plain unicode character in the doc.
            (editor as any).tf.insertNodes({ text: emoji.native });
        },
        [editor, removeInput],
    );

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        inputProps.onKeyDown(e);
        if (e.defaultPrevented) return;
        if (!filtered.length) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => (i + 1) % filtered.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => (i - 1 + filtered.length) % filtered.length);
        } else if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            const emoji = filtered[Math.min(activeIndex, filtered.length - 1)];
            if (emoji) selectEmoji(emoji);
        }
    };

    const showDropdown = query.length > 0;

    return (
        <PlateElement {...props} as="span" attributes={{ ...props.attributes }}>
            <span ref={wrapRef} contentEditable={false} className={styles.Wrap}>
                <input
                    ref={inputRef}
                    className={styles.Input}
                    value={query}
                    aria-label="Emoji search"
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setActiveIndex(0);
                    }}
                    onKeyDown={onKeyDown}
                    onBlur={inputProps.onBlur}
                />
            </span>
            {showDropdown &&
                createPortal(
                    <span
                        className={styles.Dropdown}
                        role="listbox"
                        style={
                            pos
                                ? { position: 'fixed', left: pos.left, top: pos.top, bottom: pos.bottom }
                                : { position: 'fixed', visibility: 'hidden' }
                        }
                    >
                        {filtered.length === 0 ? (
                            <span className={styles.Empty}>{library ? 'No matching emoji' : 'Loading…'}</span>
                        ) : (
                            filtered.map((emoji, i) => (
                                <span
                                    key={emoji.id}
                                    role="option"
                                    aria-selected={i === activeIndex}
                                    className={clsx(styles.Row, i === activeIndex && styles.RowActive)}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        selectEmoji(emoji);
                                    }}
                                    onMouseEnter={() => setActiveIndex(i)}
                                >
                                    <span className={styles.Native}>{emoji.native}</span>
                                    <span className={styles.Label}>{emoji.name}</span>
                                </span>
                            ))
                        )}
                    </span>,
                    document.body,
                )}
            {props.children}
        </PlateElement>
    );
}
